import type { ReactNode } from 'react';
import { getWebsiteUrl } from '../config';
import { SiteFooter } from './SiteFooter';

/**
 * Tenant identity shown in the header: the industry-standard trust-center
 * layout (verified: trust.openai.com/SafeBase, trust.monaco.com/Vanta):
 * the CUSTOMER's brand leads the page even on vendor-hosted domains, and the
 * vendor appears once as "Powered by X". The page is the tenant's trust
 * center; ComplyEdge converts visitors through the powered-by slot and the
 * bottom CTA, not by occupying the chrome.
 */
export interface TrustShellTenant {
  name: string;
  websiteUrl?: string | null;
}

export function TrustShell({
  children,
  tenant,
}: {
  children: ReactNode;
  tenant?: TrustShellTenant;
}) {
  const websiteUrl = getWebsiteUrl();

  return (
    <div className="trust-page-bg min-h-screen flex flex-col">
      <header className="sticky top-0 z-20 border-b border-white/10 bg-dark/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between gap-4 px-5 sm:px-6 lg:px-8">
          {tenant ? (
            // min-w-0 + truncate: a long tenant name stays on one line inside
            // the h-16 bar instead of wrapping out of it on a phone.
            <span className="min-w-0 font-display text-lg font-bold leading-tight">
              <span className="block truncate text-light" title={tenant.name}>{tenant.name}</span>
              <span className="mt-0.5 block font-mono text-[10px] font-normal uppercase tracking-[0.18em] text-primary">
                AI Trust Center
              </span>
            </span>
          ) : (
            <a
              href={websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 transition-opacity hover:opacity-90"
            >
              <img
                src={`${websiteUrl}/logo-transparent.png`}
                alt="ComplyEdge"
                className="h-8 w-auto"
              />
              <span className="font-display text-lg font-bold leading-tight">
                <span className="text-primary">ComplyEdge</span>
                <span className="mt-0.5 block font-mono text-[10px] font-normal uppercase tracking-[0.18em] text-primary">
                  AI Trust Center
                </span>
              </span>
            </a>
          )}

          {/*
            Industry-standard verifier slot (SafeBase: "Powered by SafeBase"
            on trust.openai.com; Vanta: same customer-first pattern on
            trust.monaco.com). One mark, one meaning: the conversion path
            into complyedge.io. "Embed seal" lives in the CTA section, not
            the chrome.
          */}
          {tenant ? (
            <a
              href={websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              // Full opacity at rest: at opacity-80 the 10px "Powered by" label
              // measured 4.0:1 on the header; at rest 5.53:1, on hover 4.72:1
              // (CE-REVIEW-TRUST-016 F1).
              className="flex shrink-0 items-center gap-2 transition-opacity hover:opacity-90"
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-gray-500">
                Powered by
              </span>
              <span className="flex items-center gap-1.5">
                <img
                  src={`${websiteUrl}/logo-transparent.png`}
                  alt=""
                  aria-hidden
                  className="h-4 w-auto"
                />
                <span className="font-display text-sm font-semibold tracking-tight text-primary">
                  ComplyEdge
                </span>
              </span>
            </a>
          ) : null}
        </div>
      </header>
      <main className="flex-1">{children}</main>
      <SiteFooter />
    </div>
  );
}
